import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getItems, getCategories, getBrands, getVendors, createItem, updateItem, deleteItem, getItemVendorPricing, addItemVendorPricing, deleteItemVendorPricing, createVendor, exportItems, checkItemDuplicate, createBrand, createCategory } from '../lib/api';
import { downloadBlob } from '../lib/downloadHelper';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { 
  Search, 
  Plus, 
  Edit, 
  Trash2, 
  Loader2, 
  Package,
  AlertCircle,
  AlertTriangle,
  DollarSign,
  TrendingDown,
  Building2,
  Download,
  Printer,
  ChevronDown,
  Check
} from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { ImageUploader, ItemThumbnail } from '../components/ImageUploader';
import { ItemsFilterBar } from './items/ItemsFilterBar';
import { AddVendorDialog, AddBrandDialog, AddCategoryDialog, AddSubCategoryDialog, AddUnitDialog } from './items/QuickAddDialogs';

const Items = () => {
  const { isManager, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedParentCategory, setSelectedParentCategory] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState('all');
  
  // Item dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    item_name: '',
    item_description: '',
    item_spec: '',
    internal_code: '',
    parent_category_id: '',
    category_id: '',
    brand_id: '',
    unit: 'PCS',
    image_ids: []
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [forceCreate, setForceCreate] = useState(false);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Pricing dialog state
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [pricings, setPricings] = useState([]);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [pricingFormData, setPricingFormData] = useState({
    vendor_id: '',
    vendor_item_name: '',
    vendor_sku: '',
    package_label: 'Single',
    units_per_package: '1',
    package_price: '',
    unit: 'PCS'
  });
  const [savingPricing, setSavingPricing] = useState(false);
  
  // Add vendor dialog state
  const [addVendorDialogOpen, setAddVendorDialogOpen] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [savingVendor, setSavingVendor] = useState(false);

  // Brand combobox state
  const [brandPopoverOpen, setBrandPopoverOpen] = useState(false);
  const [brandSearch, setBrandSearch] = useState('');

  // Add brand dialog state
  const [addBrandDialogOpen, setAddBrandDialogOpen] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [savingBrand, setSavingBrand] = useState(false);

  // Searchable dropdown states
  const [catPopoverOpen, setCatPopoverOpen] = useState(false);
  const [catSearch, setCatSearch] = useState('');
  const [subCatPopoverOpen, setSubCatPopoverOpen] = useState(false);
  const [subCatSearch, setSubCatSearch] = useState('');
  const [unitPopoverOpen, setUnitPopoverOpen] = useState(false);
  const [unitSearch, setUnitSearch] = useState('');

  // Add category/subcategory/unit dialog states
  const [addCategoryDialogOpen, setAddCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryCode, setNewCategoryCode] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);

  const [addSubCatDialogOpen, setAddSubCatDialogOpen] = useState(false);
  const [newSubCatName, setNewSubCatName] = useState('');
  const [newSubCatCode, setNewSubCatCode] = useState('');
  const [savingSubCat, setSavingSubCat] = useState(false);

  const [addUnitDialogOpen, setAddUnitDialogOpen] = useState(false);
  const [newUnitValue, setNewUnitValue] = useState('');

  // Predefined + custom units
  const defaultUnits = ['PCS', 'BOX', 'FT', 'METER', 'ROLL', 'SET', 'PAIR', 'EACH', 'LB', 'KG', 'GAL'];
  const [customUnits, setCustomUnits] = useState([]);
  const allUnits = [...new Set([...defaultUnits, ...customUnits])];

  const fetchData = async () => {
    setLoading(true);
    try {
      const [itemsRes, catRes, brandRes, vendorRes] = await Promise.all([
        getItems({
          search: searchQuery || undefined,
          category_id: selectedCategory !== 'all' ? selectedCategory : undefined,
          brand_id: selectedBrand !== 'all' ? selectedBrand : undefined
        }),
        getCategories(),
        getBrands(),
        getVendors(true)
      ]);
      
      setItems(itemsRes.data);
      setCategories(catRes.data);
      setBrands(brandRes.data);
      setVendors(vendorRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };  useEffect(() => {
    fetchData();
  }, [selectedCategory, selectedBrand]);

  // Auto-open pricing dialog when redirected from restock page
  useEffect(() => {
    if (searchParams.get('set_pricing') === 'true' && items.length > 0 && vendors.length > 0) {
      const itemId = searchParams.get('item_id');
      const vendorId = searchParams.get('vendor_id');
      const item = items.find(i => i.id === itemId);
      if (item) {
        setSelectedItem(item);
        setPricingDialogOpen(true);
        fetchPricing(item.id);
        setPricingFormData(prev => ({
          ...prev,
          vendor_id: vendorId || prev.vendor_id,
          vendor_item_name: item.item_name,
          unit: item.unit
        }));
      }
    }
  }, [items, vendors, searchParams]);

  // When parent category changes, reset subcategory filter
  useEffect(() => {
    setSelectedCategory('all');
  }, [selectedParentCategory]);

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      fetchData();
    }, 300);
    return () => clearTimeout(delayedSearch);
  }, [searchQuery]);

  // Category hierarchy helpers
  const parentCategories = categories.filter(c => !c.parent_id);
  const getSubcategories = (parentId) => categories.filter(c => c.parent_id === parentId);
  
  // Filtered subcategories for filter dropdown
  const filterSubcategories = selectedParentCategory !== 'all'
    ? getSubcategories(selectedParentCategory)
    : categories.filter(c => c.parent_id);

  // For item filtering: if parent selected but no sub, filter by all children
  const filteredItems = items.filter(item => {
    if (selectedParentCategory !== 'all' && selectedCategory === 'all') {
      const childIds = getSubcategories(selectedParentCategory).map(c => c.id);
      return childIds.includes(item.category_id);
    }
    return true;
  });

  const fetchPricing = async (itemId) => {
    setLoadingPricing(true);
    try {
      const res = await getItemVendorPricing(itemId);
      setPricings(res.data);
    } catch (error) {
      toast.error('Failed to load pricing');
    } finally {
      setLoadingPricing(false);
    }
  };

  const openCreateDialog = () => {
    setEditingItem(null);
    const firstParent = parentCategories[0];
    const firstSub = firstParent ? getSubcategories(firstParent.id)[0] : null;
    setFormData({
      item_name: '',
      item_description: '',
      item_spec: '',
      internal_code: '',
      parent_category_id: firstParent?.id || '',
      category_id: firstSub?.id || '',
      brand_id: brands[0]?.id || '',
      unit: 'PCS',
      image_ids: []
    });
    setError('');
    setDuplicateWarning(null);
    setForceCreate(false);
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    const itemCat = categories.find(c => c.id === item.category_id);
    const parentId = itemCat?.parent_id || '';
    setFormData({
      item_name: item.item_name,
      item_description: item.item_description || '',
      item_spec: item.item_spec,
      internal_code: item.internal_code || '',
      parent_category_id: parentId,
      category_id: item.category_id,
      brand_id: item.brand_id,
      unit: item.unit,
      image_ids: item.image_ids || []
    });
    setError('');
    setDuplicateWarning(null);
    setForceCreate(false);
    setDialogOpen(true);
  };

  const openPricingDialog = async (item) => {
    setSelectedItem(item);
    setPricingFormData({
      vendor_id: vendors[0]?.id || '',
      vendor_item_name: item.item_name,
      vendor_sku: '',
      package_label: 'Single',
      units_per_package: '1',
      package_price: '',
      unit: item.unit
    });
    setPricingError('');
    setPricingSuccess('');
    setPricingDialogOpen(true);
    await fetchPricing(item.id);
  };

  const handleSave = async () => {
    if (!formData.item_name || !formData.item_spec || !formData.category_id || !formData.brand_id || !formData.unit) {
      setError('Please fill in all required fields (Name, Spec, Subcategory, Brand, Unit)');
      return;
    }

    setSaving(true);
    setError('');
    
    try {
      const payload = {
        item_name: formData.item_name.trim(),
        item_description: formData.item_description,
        item_spec: formData.item_spec.trim(),
        internal_code: formData.internal_code ? formData.internal_code.trim().toUpperCase().replace(/\s/g, '') : null,
        category_id: formData.category_id,
        brand_id: formData.brand_id,
        unit: formData.unit,
        image_ids: formData.image_ids || []
      };

      // Check for near-duplicates (if not already confirmed)
      if (!forceCreate) {
        const dupCheck = await checkItemDuplicate({
          internal_code: payload.internal_code,
          item_name: payload.item_name,
          brand_id: payload.brand_id,
          item_spec: payload.item_spec,
          exclude_id: editingItem?.id || null
        });
        
        if (dupCheck.data.code_exists) {
          setError(`An item with internal code '${payload.internal_code}' already exists: ${dupCheck.data.code_item_name}`);
          setSaving(false);
          return;
        }
        
        if (dupCheck.data.near_duplicate) {
          setDuplicateWarning(dupCheck.data.near_duplicate);
          setSaving(false);
          return;
        }
      }

      if (editingItem) {
        await updateItem(editingItem.id, payload);
      } else {
        await createItem(payload);
      }
      setDialogOpen(false);
      setDuplicateWarning(null);
      setForceCreate(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save item');
    } finally {
      setSaving(false);
    }
  };

  const handleForceCreate = () => {
    setForceCreate(true);
    setDuplicateWarning(null);
    // Re-trigger save with force flag
    setTimeout(() => handleSave(), 50);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    setDeleting(true);
    try {
      await deleteItem(itemToDelete.id);
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete item');
    } finally {
      setDeleting(false);
    }
  };

  // Pricing error/success state
  const [pricingError, setPricingError] = useState('');
  const [pricingSuccess, setPricingSuccess] = useState('');

  const handleExportItems = async () => {
    setExporting(true);
    try {
      const params = {};
      if (selectedCategory !== 'all') params.category_id = selectedCategory;
      if (selectedBrand !== 'all') params.brand_id = selectedBrand;
      const res = await exportItems(params);
      downloadBlob(res.data, `items_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      alert('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleAddPricing = async () => {
    setPricingError('');
    setPricingSuccess('');
    if (!pricingFormData.vendor_id) {
      setPricingError('Please select a vendor');
      return;
    }
    const pkgPrice = parseFloat(pricingFormData.package_price);
    const unitsPerPkg = parseInt(pricingFormData.units_per_package) || 1;
    if (!pkgPrice || pkgPrice <= 0) {
      setPricingError('Please enter a valid package price');
      return;
    }
    if (unitsPerPkg < 1) {
      setPricingError('Units per package must be at least 1');
      return;
    }
    
    setSavingPricing(true);
    try {
      await addItemVendorPricing(selectedItem.id, {
        vendor_id: pricingFormData.vendor_id,
        vendor_item_name: pricingFormData.vendor_item_name,
        vendor_sku: pricingFormData.vendor_sku,
        package_label: pricingFormData.package_label || 'Single',
        units_per_package: unitsPerPkg,
        package_price: pkgPrice,
        unit: pricingFormData.unit
      });
      setPricingFormData({
        vendor_id: pricingFormData.vendor_id,
        vendor_item_name: selectedItem.item_name,
        vendor_sku: '',
        package_label: 'Single',
        units_per_package: '1',
        package_price: '',
        unit: selectedItem.unit
      });
      setPricingSuccess('Pricing saved successfully');
      setTimeout(() => setPricingSuccess(''), 3000);
      await fetchPricing(selectedItem.id);
    } catch (err) {
      setPricingError(err.response?.data?.detail || 'Failed to save pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const handleDeletePricing = async (pricingId) => {
    try {
      await deleteItemVendorPricing(pricingId);
      await fetchPricing(selectedItem.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete pricing');
    }
  };

  const handleAddVendor = async () => {
    if (!newVendorName.trim()) return;
    
    setSavingVendor(true);
    try {
      const res = await createVendor({ vendor_name: newVendorName, active: true });
      setVendors([...vendors, res.data]);
      setPricingFormData({ ...pricingFormData, vendor_id: res.data.id });
      setNewVendorName('');
      setAddVendorDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add vendor');
    } finally {
      setSavingVendor(false);
    }
  };

  const handleAddBrand = async () => {
    if (!newBrandName.trim()) return;
    setSavingBrand(true);
    try {
      const res = await createBrand({ brand_name: newBrandName.trim() });
      const updatedBrands = [...brands, res.data];
      setBrands(updatedBrands);
      setFormData(prev => ({ ...prev, brand_id: res.data.id }));
      setNewBrandName('');
      setAddBrandDialogOpen(false);
      toast.success(`Brand "${res.data.brand_name}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create brand');
    } finally {
      setSavingBrand(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim() || !newCategoryCode.trim()) return;
    setSavingCategory(true);
    try {
      const res = await createCategory({ category_name: newCategoryName.trim(), category_code: newCategoryCode.trim().toUpperCase(), parent_id: null });
      setCategories(prev => [...prev, res.data]);
      setFormData(prev => ({ ...prev, parent_category_id: res.data.id, category_id: '' }));
      setNewCategoryName('');
      setNewCategoryCode('');
      setAddCategoryDialogOpen(false);
      toast.success(`Category "${res.data.category_name}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create category');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleAddSubCat = async () => {
    if (!newSubCatName.trim() || !newSubCatCode.trim() || !formData.parent_category_id) return;
    setSavingSubCat(true);
    try {
      const res = await createCategory({ category_name: newSubCatName.trim(), category_code: newSubCatCode.trim().toUpperCase(), parent_id: formData.parent_category_id });
      setCategories(prev => [...prev, res.data]);
      const newFormData = { ...formData, category_id: res.data.id };
      if (editingItem) newFormData.internal_code = '';
      setFormData(newFormData);
      setNewSubCatName('');
      setNewSubCatCode('');
      setAddSubCatDialogOpen(false);
      toast.success(`Subcategory "${res.data.category_name}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create subcategory');
    } finally {
      setSavingSubCat(false);
    }
  };

  const handleAddUnit = () => {
    const unit = newUnitValue.trim().toUpperCase();
    if (!unit) return;
    if (!allUnits.includes(unit)) {
      setCustomUnits(prev => [...prev, unit]);
    }
    setFormData(prev => ({ ...prev, unit }));
    setNewUnitValue('');
    setAddUnitDialogOpen(false);
    toast.success(`Unit "${unit}" added`);
  };

  const getCategoryName = (id) => {
    const cat = categories.find(c => c.id === id);
    if (!cat) return 'Unknown';
    const parent = cat.parent_id ? categories.find(c => c.id === cat.parent_id) : null;
    return parent ? `${parent.category_name} > ${cat.category_name}` : cat.category_name;
  };
  const getBrandName = (id) => brands.find(b => b.id === id)?.brand_name || 'Unknown';

  const getLowestPrice = (itemId) => {
    // This would need to be fetched, for now return null
    return null;
  };

  return (
    <div className="space-y-6 animate-slide-in" data-testid="items-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="heading-2 text-[#1F2937]">Item Catalog</h1>
          <p className="text-[#6B7280]">Manage your inventory items and vendor pricing</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button onClick={handleExportItems} disabled={exporting || items.length === 0}
            className="gap-2 bg-green-600 hover:bg-green-700 text-white rounded-lg" data-testid="export-items-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={18} />}
            Export Excel
          </Button>
          <Button onClick={() => window.print()} variant="outline"
            className="gap-2 rounded-lg border-[#0776BD] text-[#0776BD]" data-testid="print-items-btn">
            <Printer size={18} /> Print
          </Button>
          {isManager() && (
            <Button onClick={openCreateDialog} className="gap-2 bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="add-item-btn">
              <Plus size={18} /> Add Item
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <ItemsFilterBar
        searchQuery={searchQuery} setSearchQuery={setSearchQuery}
        selectedParentCategory={selectedParentCategory} setSelectedParentCategory={setSelectedParentCategory}
        selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
        selectedBrand={selectedBrand} setSelectedBrand={setSelectedBrand}
        parentCategories={parentCategories}
        filterSubcategories={filterSubcategories}
        brands={brands}
      />

      {/* Items Table */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
        <CardHeader>
          <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
            <Package size={20} className="text-[#0776BD]" />
            Items ({filteredItems.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="py-12 text-center text-[#6B7280]">
              <Package size={48} className="mx-auto mb-4 opacity-50" />
              <p>No items found</p>
              {isManager() && <p className="text-sm">Click "Add Item" to create one</p>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F6F7]">
                    <TableHead className="font-semibold text-[#1F2937]">Item Name</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Internal Code</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Category</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Brand</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Unit</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-center">Status</TableHead>
                    {isManager() && <TableHead className="font-semibold text-[#1F2937] text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredItems.map((item) => (
                    <TableRow key={item.id} className="hover:bg-[#F5F6F7]" data-testid={`item-row-${item.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <ItemThumbnail imageIds={item.image_ids} size={40} alt={item.item_name} />
                          <div>
                            <p className="font-medium text-[#1F2937]">{item.item_name}</p>
                            {item.item_description && (
                              <p className="text-sm text-[#6B7280] truncate max-w-[200px]">{item.item_description}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-[#0776BD]">{item.internal_code}</TableCell>
                      <TableCell className="text-[#6B7280]">{getCategoryName(item.category_id)}</TableCell>
                      <TableCell className="text-[#6B7280]">{getBrandName(item.brand_id)}</TableCell>
                      <TableCell className="text-[#6B7280]">{item.unit}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={item.active ? "bg-green-100 text-green-800 border-0" : "bg-gray-100 text-gray-600 border-0"}>
                          {item.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      {isManager() && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openPricingDialog(item)}
                              className="hover:bg-[#F0C500]/20 text-[#F0C500]"
                              title="Vendor Pricing"
                              data-testid={`pricing-item-${item.id}`}
                            >
                              <DollarSign size={18} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openEditDialog(item)}
                              className="hover:bg-[#E6F2FA] text-[#0776BD]"
                              data-testid={`edit-item-${item.id}`}
                            >
                              <Edit size={18} />
                            </Button>
                            {isAdmin() && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => { setItemToDelete(item); setDeleteDialogOpen(true); }}
                                className="hover:bg-red-50 text-red-500"
                                data-testid={`delete-item-${item.id}`}
                              >
                                <Trash2 size={18} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Item Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg rounded-xl overflow-visible">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937]">
              {editingItem ? 'Edit Item' : 'Add New Item'}
            </DialogTitle>
          </DialogHeader>
          
          {error && (
            <Alert variant="destructive" className="rounded-lg">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {duplicateWarning && (
            <Alert className="rounded-lg border-amber-300 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <p className="font-medium">This item may already exist</p>
                <p className="text-sm mt-1">Found: <span className="font-semibold">{duplicateWarning.item_name}</span> ({duplicateWarning.internal_code})</p>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={() => setDuplicateWarning(null)} className="h-8 text-xs rounded-lg">Go Back</Button>
                  <Button size="sm" onClick={handleForceCreate} className="h-8 text-xs bg-amber-600 hover:bg-amber-700 rounded-lg" data-testid="force-create-btn">Create Anyway</Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Internal Code</Label>
              <Input
                value={formData.internal_code}
                onChange={(e) => setFormData({ ...formData, internal_code: e.target.value.toUpperCase().replace(/\s/g, '') })}
                placeholder="Auto-generated if left blank"
                className="h-10 rounded-lg border-gray-300 focus:border-[#0776BD] font-mono"
                data-testid="item-code-input"
              />
              <p className="text-xs text-[#9CA3AF]">Must be unique. Leave blank to auto-generate.</p>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Item Name *</Label>
              <Input
                value={formData.item_name}
                onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                placeholder="e.g., Single Pole Switch 15A"
                className="h-10 rounded-lg border-gray-300 focus:border-[#0776BD]"
                data-testid="item-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Item Spec *</Label>
              <Input
                value={formData.item_spec}
                onChange={(e) => {
                  const newFormData = { ...formData, item_spec: e.target.value };
                  if (editingItem) newFormData.internal_code = '';
                  setFormData(newFormData);
                }}
                placeholder="e.g., 15A-120V"
                className="h-10 rounded-lg border-gray-300 focus:border-[#0776BD]"
                data-testid="item-spec-input"
              />
            </div>
            
            {/* Category */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#1F2937]">Category *</Label>
                <div className="flex gap-1.5">
                  <Popover open={catPopoverOpen} onOpenChange={setCatPopoverOpen} modal={true}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="h-10 flex-1 justify-between rounded-lg border-gray-300 font-normal text-left truncate" data-testid="item-parent-category-select">
                        <span className="truncate">{formData.parent_category_id ? parentCategories.find(c => c.id === formData.parent_category_id)?.category_name || 'Select' : 'Select category'}</span>
                        <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[220px] p-0" align="start" side="bottom" avoidCollisions={true} sideOffset={4}>
                      <Command>
                        <CommandInput placeholder="Search..." value={catSearch} onValueChange={setCatSearch} />
                        <CommandList className="max-h-[200px]">
                          <CommandEmpty>
                            <span className="text-sm text-[#6B7280]">No results. Click + to add.</span>
                          </CommandEmpty>
                          <CommandGroup>
                            {parentCategories.map(cat => (
                              <CommandItem key={cat.id} value={cat.category_name} onSelect={() => {
                                const subs = getSubcategories(cat.id);
                                const f = { ...formData, parent_category_id: cat.id, category_id: subs[0]?.id || '' };
                                if (editingItem) f.internal_code = '';
                                setFormData(f);
                                setCatPopoverOpen(false);
                                setCatSearch('');
                              }}>
                                <Check className={`mr-2 h-3.5 w-3.5 ${formData.parent_category_id === cat.id ? 'opacity-100' : 'opacity-0'}`} />
                                {cat.category_name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0 border-gray-300 hover:bg-[#E6F2FA]" onClick={() => { setNewCategoryName(''); setNewCategoryCode(''); setAddCategoryDialogOpen(true); }} title="Add Category" data-testid="add-category-btn">
                    <Plus size={16} />
                  </Button>
                </div>
              </div>

              {/* Subcategory */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#1F2937]">Subcategory *</Label>
                <div className="flex gap-1.5">
                  <Popover open={subCatPopoverOpen} onOpenChange={setSubCatPopoverOpen} modal={true}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="h-10 flex-1 justify-between rounded-lg border-gray-300 font-normal text-left truncate" disabled={!formData.parent_category_id} data-testid="item-category-select">
                        <span className="truncate">{formData.category_id ? categories.find(c => c.id === formData.category_id)?.category_name || 'Select' : 'Select subcategory'}</span>
                        <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[220px] p-0" align="start" side="bottom" avoidCollisions={true} sideOffset={4}>
                      <Command>
                        <CommandInput placeholder="Search..." value={subCatSearch} onValueChange={setSubCatSearch} />
                        <CommandList className="max-h-[200px]">
                          <CommandEmpty>
                            <span className="text-sm text-[#6B7280]">No results. Click + to add.</span>
                          </CommandEmpty>
                          <CommandGroup>
                            {(formData.parent_category_id ? getSubcategories(formData.parent_category_id) : []).map(cat => (
                              <CommandItem key={cat.id} value={cat.category_name} onSelect={() => {
                                const f = { ...formData, category_id: cat.id };
                                if (editingItem) f.internal_code = '';
                                setFormData(f);
                                setSubCatPopoverOpen(false);
                                setSubCatSearch('');
                              }}>
                                <Check className={`mr-2 h-3.5 w-3.5 ${formData.category_id === cat.id ? 'opacity-100' : 'opacity-0'}`} />
                                {cat.category_name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0 border-gray-300 hover:bg-[#E6F2FA]" disabled={!formData.parent_category_id} onClick={() => { setNewSubCatName(''); setNewSubCatCode(''); setAddSubCatDialogOpen(true); }} title="Add Subcategory" data-testid="add-subcategory-btn">
                    <Plus size={16} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Brand & Unit */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#1F2937]">Brand *</Label>
                <div className="flex gap-1.5">
                  <Popover open={brandPopoverOpen} onOpenChange={setBrandPopoverOpen} modal={true}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="h-10 flex-1 justify-between rounded-lg border-gray-300 font-normal text-left truncate" data-testid="item-brand-select">
                        <span className="truncate">{formData.brand_id ? brands.find(b => b.id === formData.brand_id)?.brand_name || 'Select' : 'Select brand'}</span>
                        <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[220px] p-0" align="start" side="bottom" avoidCollisions={true} sideOffset={4}>
                      <Command>
                        <CommandInput placeholder="Search..." value={brandSearch} onValueChange={setBrandSearch} />
                        <CommandList className="max-h-[200px]">
                          <CommandEmpty>
                            <span className="text-sm text-[#6B7280]">No results. Click + to add.</span>
                          </CommandEmpty>
                          <CommandGroup>
                            {brands.map(brand => (
                              <CommandItem key={brand.id} value={brand.brand_name} onSelect={() => {
                                const f = { ...formData, brand_id: brand.id };
                                if (editingItem) f.internal_code = '';
                                setFormData(f);
                                setBrandPopoverOpen(false);
                                setBrandSearch('');
                              }}>
                                <Check className={`mr-2 h-3.5 w-3.5 ${formData.brand_id === brand.id ? 'opacity-100' : 'opacity-0'}`} />
                                {brand.brand_name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0 border-gray-300 hover:bg-[#E6F2FA]" onClick={() => { setNewBrandName(''); setAddBrandDialogOpen(true); }} title="Add Brand" data-testid="add-brand-btn">
                    <Plus size={16} />
                  </Button>
                </div>
              </div>

              {/* Unit */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#1F2937]">Unit *</Label>
                <div className="flex gap-1.5">
                  <Popover open={unitPopoverOpen} onOpenChange={setUnitPopoverOpen} modal={true}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="h-10 flex-1 justify-between rounded-lg border-gray-300 font-normal text-left truncate" data-testid="item-unit-select">
                        <span className="truncate">{formData.unit || 'Select unit'}</span>
                        <ChevronDown className="ml-1 h-3.5 w-3.5 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[220px] p-0" align="start" side="bottom" avoidCollisions={true} sideOffset={4}>
                      <Command>
                        <CommandInput placeholder="Search..." value={unitSearch} onValueChange={setUnitSearch} />
                        <CommandList className="max-h-[200px]">
                          <CommandEmpty>
                            <span className="text-sm text-[#6B7280]">No results. Click + to add.</span>
                          </CommandEmpty>
                          <CommandGroup>
                            {allUnits.map(unit => (
                              <CommandItem key={unit} value={unit} onSelect={() => {
                                setFormData(prev => ({ ...prev, unit }));
                                setUnitPopoverOpen(false);
                                setUnitSearch('');
                              }}>
                                <Check className={`mr-2 h-3.5 w-3.5 ${formData.unit === unit ? 'opacity-100' : 'opacity-0'}`} />
                                {unit}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button type="button" variant="outline" size="icon" className="h-10 w-10 shrink-0 border-gray-300 hover:bg-[#E6F2FA]" onClick={() => { setNewUnitValue(''); setAddUnitDialogOpen(true); }} title="Add Unit" data-testid="add-unit-btn">
                    <Plus size={16} />
                  </Button>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Description</Label>
              <Input
                value={formData.item_description}
                onChange={(e) => setFormData({ ...formData, item_description: e.target.value })}
                placeholder="Optional description"
                className="h-10 rounded-lg border-gray-300 focus:border-[#0776BD]"
                data-testid="item-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-[#1F2937]">Photos</Label>
              <ImageUploader
                imageIds={formData.image_ids || []}
                onChange={(ids) => setFormData(prev => ({ ...prev, image_ids: ids }))}
                max={5}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0776BD] hover:bg-[#055A92] rounded-lg" data-testid="save-item-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingItem ? 'Update Item' : 'Create Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937]">Delete Item</DialogTitle>
          </DialogHeader>
          <p className="text-[#6B7280]">Are you sure you want to delete "{itemToDelete?.item_name}"? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="rounded-lg">Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} className="rounded-lg" data-testid="confirm-delete-btn">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Vendor Pricing Dialog */}
      <Dialog open={pricingDialogOpen} onOpenChange={setPricingDialogOpen}>
        <DialogContent className="max-w-2xl rounded-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#1F2937] flex items-center gap-2">
              <DollarSign className="text-[#F0C500]" />
              Vendor Pricing - {selectedItem?.item_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add Pricing Form */}
            <div className="p-4 bg-[#F5F6F7] rounded-lg">
              <p className="text-sm font-medium text-[#1F2937] mb-3">Add Vendor Package Pricing</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex gap-2">
                  <Select 
                    value={pricingFormData.vendor_id} 
                    onValueChange={(val) => setPricingFormData({ ...pricingFormData, vendor_id: val })}
                  >
                    <SelectTrigger className="h-10 rounded-lg border-gray-300 flex-1" data-testid="pricing-vendor-select">
                      <SelectValue placeholder="Select Vendor *" />
                    </SelectTrigger>
                    <SelectContent>
                      {vendors.map(vendor => (
                        <SelectItem key={vendor.id} value={vendor.id}>{vendor.vendor_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {isManager() && (
                    <Button 
                      variant="outline" 
                      size="icon" 
                      className="h-10 w-10"
                      onClick={() => setAddVendorDialogOpen(true)}
                      title="Add New Vendor"
                    >
                      <Plus size={16} />
                    </Button>
                  )}
                </div>
                <Input
                  value={pricingFormData.package_label}
                  onChange={(e) => setPricingFormData({ ...pricingFormData, package_label: e.target.value })}
                  placeholder="Package Label (e.g. Single, 4-Pack)"
                  className="h-10 rounded-lg border-gray-300"
                  data-testid="pricing-package-label"
                />
                <Input
                  type="number"
                  min="1"
                  value={pricingFormData.units_per_package}
                  onChange={(e) => setPricingFormData({ ...pricingFormData, units_per_package: e.target.value })}
                  placeholder="Units per Package *"
                  className="h-10 rounded-lg border-gray-300"
                  data-testid="pricing-units-per-pkg"
                />
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    value={pricingFormData.package_price}
                    onChange={(e) => setPricingFormData({ ...pricingFormData, package_price: e.target.value })}
                    placeholder="Package Price *"
                    className="h-10 rounded-lg border-gray-300"
                    data-testid="pricing-package-price"
                  />
                  {pricingFormData.package_price && parseInt(pricingFormData.units_per_package) > 0 && (
                    <span className="absolute right-2 top-2.5 text-xs text-[#0776BD] font-medium">
                      = ${(parseFloat(pricingFormData.package_price) / parseInt(pricingFormData.units_per_package || 1)).toFixed(2)}/unit
                    </span>
                  )}
                </div>
                <Input
                  value={pricingFormData.vendor_sku}
                  onChange={(e) => setPricingFormData({ ...pricingFormData, vendor_sku: e.target.value })}
                  placeholder="Vendor SKU (optional)"
                  className="h-10 rounded-lg border-gray-300"
                  data-testid="pricing-vendor-sku"
                />
                <Input
                  value={pricingFormData.vendor_item_name}
                  onChange={(e) => setPricingFormData({ ...pricingFormData, vendor_item_name: e.target.value })}
                  placeholder="Vendor Item Name"
                  className="h-10 rounded-lg border-gray-300"
                />
              </div>
              <Button 
                onClick={handleAddPricing} 
                disabled={savingPricing || !pricingFormData.vendor_id || !pricingFormData.package_price}
                className="mt-3 bg-[#0776BD] hover:bg-[#055A92] rounded-lg"
                data-testid="pricing-save-btn"
              >
                {savingPricing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
                Add Pricing
              </Button>
              {pricingError && <p className="text-red-500 text-sm mt-2" data-testid="pricing-error">{pricingError}</p>}
              {pricingSuccess && <p className="text-green-600 text-sm mt-2" data-testid="pricing-success">{pricingSuccess}</p>}
            </div>
            
            {/* Pricing List */}
            {loadingPricing ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-[#0776BD]" />
              </div>
            ) : pricings.length === 0 ? (
              <div className="py-8 text-center text-[#6B7280]">
                <Building2 size={32} className="mx-auto mb-2 opacity-50" />
                <p>No vendor pricing added yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F6F7]">
                    <TableHead className="font-semibold text-[#1F2937]">Vendor</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Package</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">SKU</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right">Pkg Price</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right">Unit Price</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pricings.map((pricing, index) => (
                    <TableRow key={pricing.id} className={index === 0 ? "bg-green-50" : ""}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {index === 0 && <TrendingDown size={16} className="text-green-600" />}
                          <span className="font-medium">{pricing.vendor_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-[#1F2937]">{pricing.package_label || 'Single'}</span>
                        <span className="text-xs text-[#6B7280] ml-1">({pricing.units_per_package || 1} unit{(pricing.units_per_package || 1) > 1 ? 's' : ''})</span>
                      </TableCell>
                      <TableCell className="text-[#6B7280] font-mono text-sm">{pricing.vendor_sku || '-'}</TableCell>
                      <TableCell className="text-right text-[#6B7280]">
                        ${(pricing.package_price || pricing.unit_price * (pricing.units_per_package || 1)).toFixed(2)}
                      </TableCell>
                      <TableCell className={`text-right font-bold ${index === 0 ? 'text-green-600' : 'text-[#1F2937]'}`}>
                        ${pricing.unit_price.toFixed(2)}
                        {index === 0 && <Badge className="ml-2 bg-green-100 text-green-800 border-0 text-xs">Lowest</Badge>}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeletePricing(pricing.id)}
                          className="hover:bg-red-50 text-red-500"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            {searchParams.get('return_to') === 'restock' && searchParams.get('state') && (
              <Button
                onClick={() => {
                  setPricingDialogOpen(false);
                  navigate(`/restock?restore=${searchParams.get('state')}`);
                }}
                className="bg-green-600 hover:bg-green-700 text-white rounded-lg gap-2"
                data-testid="return-to-restock-btn"
              >
                Return to Restock
              </Button>
            )}
            <Button variant="outline" onClick={() => setPricingDialogOpen(false)} className="rounded-lg">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Vendor Quick Dialog */}
      <AddVendorDialog
        open={addVendorDialogOpen}
        onOpenChange={setAddVendorDialogOpen}
        name={newVendorName}
        setName={setNewVendorName}
        saving={savingVendor}
        onSave={handleAddVendor}
      />

      {/* Add Brand Quick Dialog */}
      <AddBrandDialog
        open={addBrandDialogOpen}
        onOpenChange={setAddBrandDialogOpen}
        name={newBrandName}
        setName={setNewBrandName}
        saving={savingBrand}
        onSave={handleAddBrand}
      />

      {/* Add Category Quick Dialog */}
      <AddCategoryDialog
        open={addCategoryDialogOpen}
        onOpenChange={setAddCategoryDialogOpen}
        name={newCategoryName}
        setName={setNewCategoryName}
        code={newCategoryCode}
        setCode={setNewCategoryCode}
        saving={savingCategory}
        onSave={handleAddCategory}
      />

      {/* Add Subcategory Quick Dialog */}
      <AddSubCategoryDialog
        open={addSubCatDialogOpen}
        onOpenChange={setAddSubCatDialogOpen}
        parentName={parentCategories.find(c => c.id === formData.parent_category_id)?.category_name || ''}
        name={newSubCatName}
        setName={setNewSubCatName}
        code={newSubCatCode}
        setCode={setNewSubCatCode}
        saving={savingSubCat}
        onSave={handleAddSubCat}
      />

      {/* Add Unit Quick Dialog */}
      <AddUnitDialog
        open={addUnitDialogOpen}
        onOpenChange={setAddUnitDialogOpen}
        value={newUnitValue}
        setValue={setNewUnitValue}
        onSave={handleAddUnit}
      />
    </div>
  );
};

export default Items;
